import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Carrot,
  ChartBar,
  Calendar,
  Package,
  Truck,
  ArrowRight,
  Pencil,
  MagnifyingGlass,
  FilePdf,
  Upload,
  Check,
  X,
  Warehouse,
} from '@phosphor-icons/react';
import { Trash2, TrendingUp, Leaf, FileUp, AlertCircle, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const CATEGORY_OPTIONS = [
  { value: 'sebze', label: 'Sebze', color: 'bg-green-100 text-green-700' },
  { value: 'meyve', label: 'Meyve', color: 'bg-orange-100 text-orange-700' },
  { value: 'yesillik', label: 'Yeşillik', color: 'bg-emerald-100 text-emerald-700' },
];

const UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'adet', label: 'Adet' },
  { value: 'demet', label: 'Demet' },
  { value: 'kasa', label: 'Kasa' },
];

export default function VegetableManagement() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Tarih filtreleri
  const [filterMode, setFilterMode] = useState('month'); // month veya range
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // list, report
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    product_name: '',
    category: 'sebze',
    quantity: '',
    unit: 'kg',
    unit_price: '',
    supplier: '',
    notes: '',
    add_to_stock: false,
  });
  
  // Ürün listesi ve yeni ürün ekleme
  const [productList, setProductList] = useState([]);
  const [isNewProductDialogOpen, setIsNewProductDialogOpen] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    name: '',
    category: 'sebze',
    unit: 'kg',
  });
  
  // PDF Import
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDate, setPdfDate] = useState(new Date().toISOString().split('T')[0]);
  const [pdfSupplier, setPdfSupplier] = useState('');
  const [pdfAddToStock, setPdfAddToStock] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
    fetchSuggestions();
    fetchProductList();
  }, [selectedMonth, startDate, endDate, filterMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let entriesUrl = '/vegetables';
      let reportUrl = '/vegetables/report';
      
      if (filterMode === 'range' && startDate && endDate) {
        entriesUrl += `?start_date=${startDate}&end_date=${endDate}`;
        reportUrl += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        entriesUrl += `?month=${selectedMonth}`;
        reportUrl += `?month=${selectedMonth}`;
      }
      
      const [entriesRes, reportRes] = await Promise.all([
        api.get(entriesUrl),
        api.get(reportUrl)
      ]);
      setEntries(entriesRes.data);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductList = async () => {
    try {
      const res = await api.get('/vegetables/product-list');
      setProductList(res.data);
    } catch (error) {
      console.error('Ürün listesi yüklenemedi:', error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const [productsRes, suppliersRes] = await Promise.all([
        api.get('/vegetables/products'),
        api.get('/vegetables/suppliers')
      ]);
      setProductSuggestions(productsRes.data);
      setSupplierSuggestions(suppliersRes.data);
    } catch (error) {
      console.error('Öneriler yüklenemedi:', error);
    }
  };

  const handleAddNewProduct = async () => {
    if (!newProductForm.name.trim()) {
      toast.error('Ürün adı gerekli');
      return;
    }
    
    try {
      const res = await api.post(`/vegetables/product-list?name=${encodeURIComponent(newProductForm.name)}&category=${newProductForm.category}&unit=${newProductForm.unit}`);
      toast.success(`"${res.data.name}" ürün listesine eklendi`);
      setIsNewProductDialogOpen(false);
      setNewProductForm({ name: '', category: 'sebze', unit: 'kg' });
      fetchProductList();
      
      // Yeni eklenen ürünü otomatik seç
      setFormData({ 
        ...formData, 
        product_name: res.data.name,
        category: res.data.category,
        unit: res.data.unit
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ürün eklenemedi');
    }
  };

  const handleProductSelect = (productName) => {
    const product = productList.find(p => p.name === productName);
    if (product) {
      setFormData({
        ...formData,
        product_name: product.name,
        category: product.category,
        unit: product.unit,
      });
    } else {
      setFormData({ ...formData, product_name: productName });
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      product_name: '',
      category: 'sebze',
      quantity: '',
      unit: 'kg',
      unit_price: '',
      supplier: '',
      notes: '',
      add_to_stock: false,
    });
  };

  const handleSubmit = async () => {
    if (!formData.product_name || !formData.quantity || !formData.unit_price) {
      toast.error('Ürün adı, miktar ve birim fiyat gerekli');
      return;
    }

    try {
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unit_price: parseFloat(formData.unit_price),
        total_amount: parseFloat(formData.quantity) * parseFloat(formData.unit_price),
      };

      if (editingEntry) {
        await api.put(`/vegetables/${editingEntry.id}`, payload);
        toast.success('Kayıt güncellendi');
      } else {
        await api.post('/vegetables', payload);
        const stockMsg = formData.add_to_stock ? ' ve depo stoğuna eklendi' : '';
        toast.success(`Sebze girişi eklendi${stockMsg}`);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      product_name: entry.product_name,
      category: entry.category,
      quantity: entry.quantity.toString(),
      unit: entry.unit,
      unit_price: entry.unit_price.toString(),
      supplier: entry.supplier || '',
      notes: entry.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/vegetables/${id}`);
      toast.success('Kayıt silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // PDF Import Functions
  const handlePdfFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sadece PDF dosyaları kabul edilir');
      return;
    }
    
    setPdfFile(file);
    setPdfPreview(null);
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/vegetables/preview-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPdfPreview(res.data);
      if (res.data.summary?.detected_supplier) {
        setPdfSupplier(res.data.summary.detected_supplier);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF okunamadı');
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfFile) {
      toast.error('Lütfen bir PDF dosyası seçin');
      return;
    }
    
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const params = new URLSearchParams();
      params.append('date', pdfDate);
      if (pdfSupplier) params.append('supplier', pdfSupplier);
      params.append('add_to_stock', pdfAddToStock.toString());
      params.append('use_ai', 'true');
      
      const res = await api.post(`/vegetables/import-pdf?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const aiMsg = res.data.ai_used ? ' (AI)' : '';
      const stockMsg = pdfAddToStock ? ' ve depo stoğuna eklendi' : '';
      toast.success(`${res.data.summary.entry_count} ürün başarıyla aktarıldı${aiMsg}${stockMsg}! Toplam: ₺${res.data.summary.total_amount.toLocaleString('tr-TR')}`);
      setIsPdfDialogOpen(false);
      resetPdfDialog();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF aktarımı başarısız');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetPdfDialog = () => {
    setPdfFile(null);
    setPdfPreview(null);
    setPdfSupplier('');
    setPdfDate(new Date().toISOString().split('T')[0]);
    setPdfAddToStock(false);
  };

  const handleTransferToExpenses = async () => {
    if (!confirm(`${selectedMonth} ayındaki tüm sebze girişlerini gider menüsüne aktarmak istediğinize emin misiniz?`)) return;
    
    try {
      const res = await api.post(`/vegetables/transfer-to-expenses?month=${selectedMonth}`);
      toast.success(`${res.data.entry_count} kalem, toplam ₺${res.data.total_amount.toLocaleString('tr-TR')} gider menüsüne aktarıldı`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aktarım başarısız');
    }
  };

  const formatCurrency = (value) => {
    return `₺${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value || 0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  const getCategoryBadge = (category) => {
    const cat = CATEGORY_OPTIONS.find(c => c.value === category);
    return <Badge className={cat?.color || 'bg-gray-100'}>{cat?.label || category}</Badge>;
  };

  const filteredEntries = entries.filter(e => 
    !searchQuery || 
    e.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.supplier && e.supplier.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const calculatedTotal = formData.quantity && formData.unit_price 
    ? parseFloat(formData.quantity) * parseFloat(formData.unit_price) 
    : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="vegetable-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Carrot className="w-8 h-8 text-green-600" />
              {t('vegetables.title')}
            </h1>
            <p className="text-muted-foreground">{t('vegetables.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Filtre Modu Seçimi */}
            <div className="flex items-center gap-2 border rounded-lg p-1">
              <Button
                variant={filterMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('month')}
              >
                {t('common.month')}
              </Button>
              <Button
                variant={filterMode === 'range' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('range')}
              >
                {t('common.dateRange')}
              </Button>
            </div>
            
            {/* Ay veya Tarih Aralığı */}
            {filterMode === 'month' ? (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36"
                  placeholder="Başlangıç"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36"
                  placeholder="Bitiş"
                />
              </div>
            )}
            
            {isManager && (
              <>
                {/* PDF'den Aktar Butonu */}
                <Dialog open={isPdfDialogOpen} onOpenChange={(open) => { setIsPdfDialogOpen(open); if (!open) resetPdfDialog(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="pdf-import-btn" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                      <FilePdf className="w-4 h-4 mr-2" />
                      PDF'den Aktar
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FilePdf className="w-5 h-5 text-red-600" />
                        PDF Fatura Aktarımı
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Dosya Seçimi */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handlePdfFileSelect}
                          className="hidden"
                          id="pdf-upload"
                        />
                        <label htmlFor="pdf-upload" className="cursor-pointer">
                          {pdfFile ? (
                            <div className="flex items-center justify-center gap-3">
                              <FilePdf className="w-10 h-10 text-red-500" />
                              <div className="text-left">
                                <p className="font-medium">{pdfFile.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {(pdfFile.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.preventDefault(); setPdfFile(null); setPdfPreview(null); }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <FileUp className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                              <p className="font-medium">PDF faturayı seçin veya sürükleyin</p>
                              <p className="text-sm text-muted-foreground">Manav/sebze faturalarını otomatik olarak okuyup aktarır</p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {/* Tarih ve Tedarikçi */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Fatura Tarihi</Label>
                          <Input
                            type="date"
                            value={pdfDate}
                            onChange={(e) => setPdfDate(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Tedarikçi/Manav</Label>
                          <Input
                            value={pdfSupplier}
                            onChange={(e) => setPdfSupplier(e.target.value)}
                            placeholder="Otomatik algılanır veya elle girin"
                          />
                        </div>
                      </div>
                      
                      {/* Yükleniyor */}
                      {pdfLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mr-3"></div>
                          <span>PDF okunuyor...</span>
                        </div>
                      )}
                      
                      {/* Önizleme */}
                      {pdfPreview && !pdfLoading && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-700">
                                {pdfPreview.summary.entry_count} ürün bulundu
                              </span>
                            </div>
                            <span className="text-lg font-bold text-green-700">
                              ₺{pdfPreview.summary.total_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                          
                          <div className="max-h-64 overflow-y-auto border rounded-lg">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ürün</TableHead>
                                  <TableHead>Kategori</TableHead>
                                  <TableHead className="text-right">Miktar</TableHead>
                                  <TableHead className="text-right">Birim Fiyat</TableHead>
                                  <TableHead className="text-right">Toplam</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pdfPreview.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{item.product_name}</TableCell>
                                    <TableCell>{getCategoryBadge(item.category)}</TableCell>
                                    <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                                    <TableCell className="text-right">₺{item.unit_price.toLocaleString('tr-TR')}</TableCell>
                                    <TableCell className="text-right font-bold">₺{item.total_amount.toLocaleString('tr-TR')}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          
                          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-700">
                              <p className="font-medium">İmport öncesi kontrol edin:</p>
                              <ul className="list-disc list-inside mt-1">
                                <li>Ürün adları ve miktarları doğru mu?</li>
                                <li>Tarih ve tedarikçi bilgisi doğru mu?</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Butonlar */}
                      {/* Depo Stoğuna Ekle */}
                      <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Checkbox
                          id="pdf-add-to-stock-veg"
                          checked={pdfAddToStock}
                          onCheckedChange={(checked) => setPdfAddToStock(checked)}
                          data-testid="pdf-add-to-stock"
                        />
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-5 h-5 text-emerald-600" />
                          <Label htmlFor="pdf-add-to-stock-veg" className="cursor-pointer font-medium text-emerald-700">
                            Depo Stoğuna Ekle & Fiyat Güncelle
                          </Label>
                        </div>
                      </div>
                      
                      {pdfPreview && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <Sparkles className="w-4 h-4" />
                          <span>AI destekli akıllı fatura okuma aktif</span>
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => { setIsPdfDialogOpen(false); resetPdfDialog(); }}
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handlePdfImport}
                          disabled={!pdfPreview || pdfLoading}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {pdfPreview?.summary?.entry_count || 0} Ürünü Kaydet
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Sebze Ekle Butonu */}
                <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-vegetable-btn" className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Sebze Ekle
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingEntry ? 'Kaydı Düzenle' : 'Yeni Sebze Girişi'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tarih *</Label>
                        <Input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Kategori</Label>
                        <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Ürün Adı *</Label>
                      <div className="flex gap-2">
                        <Select 
                          value={formData.product_name} 
                          onValueChange={handleProductSelect}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Ürün seçin..." />
                          </SelectTrigger>
                          <SelectContent>
                            {productList.map((p) => (
                              <SelectItem key={p.id} value={p.name}>
                                <span className="flex items-center gap-2">
                                  {p.name}
                                  <span className="text-xs text-muted-foreground">({p.unit})</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => setIsNewProductDialogOpen(true)}
                          title="Yeni Ürün Ekle"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Miktar *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          placeholder="50"
                        />
                      </div>
                      <div>
                        <Label>Birim</Label>
                        <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Birim Fiyat (₺) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.unit_price}
                          onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                          placeholder="40"
                        />
                      </div>
                    </div>
                    
                    {calculatedTotal > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-green-700">Toplam Tutar:</span>
                          <span className="text-xl font-bold text-green-700">{formatCurrency(calculatedTotal)}</span>
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          {formData.quantity} {formData.unit} × ₺{formData.unit_price}/{formData.unit}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label>Tedarikçi/Manav</Label>
                      <Input
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                        placeholder="Merkez Manav, Hal..."
                        list="supplier-suggestions"
                      />
                      <datalist id="supplier-suggestions">
                        {supplierSuggestions.map((s, i) => (
                          <option key={i} value={s} />
                        ))}
                      </datalist>
                    </div>
                    
                    {/* Depo Stoğuna Ekle - Sadece yeni kayıtta */}
                    {!editingEntry && (
                      <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Checkbox
                          id="form-add-to-stock-veg"
                          checked={formData.add_to_stock}
                          onCheckedChange={(checked) => setFormData({ ...formData, add_to_stock: checked })}
                          data-testid="form-add-to-stock"
                        />
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-5 h-5 text-emerald-600" />
                          <Label htmlFor="form-add-to-stock-veg" className="cursor-pointer font-medium text-emerald-700">
                            Depo Stoğuna Ekle & Fiyat Güncelle
                          </Label>
                        </div>
                      </div>
                    )}
                    
                    <Button onClick={handleSubmit} className="w-full bg-green-600 hover:bg-green-700">
                      {editingEntry ? 'Güncelle' : 'Kaydet'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Özet Kartları */}
        {report && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-medium uppercase">Toplam Harcama</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(report.total_amount)}</p>
                    <p className="text-xs text-green-600">{report.total_entries} kalem</p>
                  </div>
                  <div className="p-3 bg-green-200 rounded-full">
                    <Carrot className="w-6 h-6 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {report.by_category?.slice(0, 3).map((cat, idx) => (
              <Card key={idx} className="border-2 border-slate-200">
                <CardContent className="pt-4 pb-4">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium uppercase">
                      {CATEGORY_OPTIONS.find(c => c.value === cat.category)?.label || cat.category}
                    </p>
                    <p className="text-xl font-bold">{formatCurrency(cat.total_amount)}</p>
                    <p className="text-xs text-muted-foreground">{cat.entry_count} kalem</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tab Butonları */}
        <div className="flex gap-2 border-b">
          <Button 
            variant={activeTab === 'list' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('list')}
            className="rounded-b-none"
          >
            <Package className="w-4 h-4 mr-2" />
            Giriş Listesi
          </Button>
          <Button 
            variant={activeTab === 'report' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('report')}
            className="rounded-b-none"
          >
            <ChartBar className="w-4 h-4 mr-2" />
            Raporlar
          </Button>
        </div>

        {/* Liste Tab */}
        {activeTab === 'list' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {getMonthName(selectedMonth)} Girişleri ({filteredEntries.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Ürün veya tedarikçi ara..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  {isAdmin && entries.length > 0 && (
                    <Button variant="outline" onClick={handleTransferToExpenses}>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Giderlere Aktar
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Carrot size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Bu ayda sebze girişi yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Ürün</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead>Tedarikçi</TableHead>
                        <TableHead className="text-center">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{formatDate(entry.date)}</TableCell>
                          <TableCell className="font-semibold">{entry.product_name}</TableCell>
                          <TableCell>{getCategoryBadge(entry.category)}</TableCell>
                          <TableCell className="text-right">{entry.quantity} {entry.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.unit_price)}/{entry.unit}</TableCell>
                          <TableCell className="text-right font-bold text-green-700">{formatCurrency(entry.total_amount)}</TableCell>
                          <TableCell className="text-muted-foreground">{entry.supplier || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              {isManager && (
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDelete(entry.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rapor Tab */}
        {activeTab === 'report' && report && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* En Çok Harcanan Ürünler */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  En Çok Harcanan Ürünler
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.by_product?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Veri yok</p>
                ) : (
                  <div className="space-y-3">
                    {report.by_product?.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                            idx === 1 ? 'bg-gray-300 text-gray-700' :
                            idx === 2 ? 'bg-orange-300 text-orange-800' :
                            'bg-slate-200 text-slate-600'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-semibold">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.total_quantity} {item.unit} • {item.entry_count} giriş
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-green-700">{formatCurrency(item.total_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tedarikçi Bazlı */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  Tedarikçi Bazlı Harcama
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.by_supplier?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Veri yok</p>
                ) : (
                  <div className="space-y-3">
                    {report.by_supplier?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{item.supplier}</p>
                          <p className="text-xs text-muted-foreground">{item.entry_count} giriş</p>
                        </div>
                        <span className="text-lg font-bold text-blue-700">{formatCurrency(item.total_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Günlük Toplamlar */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Günlük Harcamalar
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.daily_totals?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Veri yok</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    {report.daily_totals?.slice(0, 31).map((day, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">{formatDate(day.date)}</p>
                        <p className="font-bold text-green-700">{formatCurrency(day.total_amount)}</p>
                        <p className="text-xs text-muted-foreground">{day.entry_count} kalem</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Yeni Ürün Ekle Dialog */}
        <Dialog open={isNewProductDialogOpen} onOpenChange={setIsNewProductDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Yeni Ürün Ekle
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Ürün Adı *</Label>
                <Input
                  value={newProductForm.name}
                  onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                  placeholder="Örn: Pırasa, Kereviz..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kategori</Label>
                  <Select value={newProductForm.category} onValueChange={(v) => setNewProductForm({ ...newProductForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Varsayılan Birim</Label>
                  <Select value={newProductForm.unit} onValueChange={(v) => setNewProductForm({ ...newProductForm, unit: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button onClick={handleAddNewProduct} className="w-full bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Ürün Listesine Ekle
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
